import React, { useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { PackageOpen, Plus } from "lucide-react";
import { AttachContractModal } from "./AttachContractModal";
import { ViewContractModal } from "./ViewContractModal";
import { useGetContractsDuringAddList } from "@features/cx/consumerManagement/hooks";

interface ContractTabProps {
  consumerId: string;
  consumerNo: string;
  remoteUtilityId: number;
  contractData: any;
  subCategory: string;
}

const ContractTab: React.FC<ContractTabProps> = ({
  consumerId,
  consumerNo,
  remoteUtilityId,
  contractData,
  subCategory,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);

  const extractContractIds = React.useMemo(() => {
    return contractData?.result?.map((c: any) => c.id.toString()) || [];
  }, [contractData]);

  const subcategories = React.useMemo(() => {
    return subCategory ? [subCategory] : [];
  }, [subCategory]);

  const { data: availableContractsData } = useGetContractsDuringAddList({
    remoteUtilityId: remoteUtilityId.toString(),
    extractContractIds,
    subcategories,
  });

  const availableContracts = React.useMemo(() => {
    return availableContractsData?.result || [];
  }, [availableContractsData]);
  const [selectedContractForView, setSelectedContractForView] =
    useState<any>(null);

  const columns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Contract Name",
    },
    {
      key: "consumerCategoriesDisplay",
      header: "Category",
      sortable: true,
      render: (value: string[], row: any) => {
        const categories = value || row?.consumer_categories_display || row?.consumerCategoriesDisplay || [];
        const filtered = Array.isArray(categories) ? categories.filter(cat => cat !== "") : [];
        return filtered.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {filtered.map((cat, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium">
                {cat}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">N/A</span>
        );
      },
    },
    {
      key: "consumerSubCategoriesDisplay",
      header: "Sub Category",
      sortable: true,
      render: (value: string[], row: any) => {
        const subCategories = value || row?.consumer_sub_categories_display || row?.consumerSubCategoriesDisplay || [];
        const filtered = Array.isArray(subCategories) ? subCategories.filter(sub => sub !== "") : [];
        return filtered.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {filtered.map((sub, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium">
                {sub}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">N/A</span>
        );
      },
    },
    {
      key: "createdDate",
      header: "Attached On",
    },
    {
      key: "file",
      header: "Attached Files",
      render: (value) =>
        value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            View File
          </a>
        ) : (
          <span className="text-gray-400">No file</span>
        ),
    },
  ];

  const actions = [
    {
      label: "View",
      icon: "Eye" as const,
      onClick: (row: any) => setSelectedContractForView(row),
    },
  ];

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const filteredContracts = React.useMemo(() => {
    const contracts = contractData?.result || [];
    if (!searchTerm) return contracts;
    const lowerSearch = searchTerm.toLowerCase();
    return contracts.filter(
      (contract: any) =>
        contract?.name?.toLowerCase().includes(lowerSearch) ||
        contract?.id?.toString().includes(lowerSearch) ||
        contract?.createdDate?.includes(searchTerm) ||
        contract?.consumerCategoriesDisplay?.some((cat: string) => cat.toLowerCase().includes(lowerSearch)) ||
        contract?.consumerSubCategoriesDisplay?.some((sub: string) => sub.toLowerCase().includes(lowerSearch))
    );
  }, [contractData, searchTerm]);

  return (
    <div className="space-y-4">
      {/* Header and Attach Button */}
      <div className="flex items-center justify-between">
        <PageHeader
          title="Contracts"
          subtitle="Attach existing contracts to this account."
        />
        <Button
          variant="default"
          onClick={() => setIsAttachModalOpen(true)}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Attach Contract
        </Button>
      </div>

      {/* Generic Table */}
      <GenericTable
        data={filteredContracts}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        searchPlaceholder="Search contracts..."
        searchParamName="search"
        searchDebounceMs={500}
        onSearch={handleSearch}
        emptyMessage="No contracts found"
      />

      {/* Modals */}
      <AttachContractModal
        isOpen={isAttachModalOpen}
        onClose={() => setIsAttachModalOpen(false)}
        onAttach={(contract) => {
          console.log("Attached contract:", contract);
        }}
        availableContracts={availableContracts}
        consumerId={consumerId}
      />

      <ViewContractModal
        isOpen={!!selectedContractForView}
        onClose={() => setSelectedContractForView(null)}
        contract={selectedContractForView}
      />
    </div>
  );
};

export default ContractTab;
